import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  InputAdornment,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ProductGroupTree from './ProductGroupTree';
import { filterActiveIngredientOptions } from '../../utils/activeIngredientSearch';

interface ProductGroupListItem {
  id: string;
  name: string;
  archivedDate?: string;
  activeIngredientDetails: string[];
  memberProductCount: number;
  parentProductGroupId?: string;
  parentProductGroupName?: string;
}

interface ProductGroupActiveIngredient {
  id: string;
  activeIngredientId: string;
  activeIngredientName: string;
  unitId: string;
  unitName: string;
  unitAmount: number;
  isVisible: boolean;
}

interface ProductGroupDetail {
  id: string;
  name: string;
  archivedDate?: string;
  parentProductGroupId?: string;
  parentProductGroupName?: string;
  childProductGroups: { id: string; name: string }[];
  activeIngredients: ProductGroupActiveIngredient[];
  memberProducts: { id: string; name: string }[];
}

interface LookupItem {
  id: string;
  name: string;
}

interface MemberProductOption {
  id: string;
  name: string;
  activeIngredientDetails?: string[];
}

interface SuggestedProduct {
  id: string;
  name: string;
  manufacturerName?: string;
  matchingActiveIngredientCount: number;
  activeIngredientDetails: string[];
}

const chipSx = { height: 20, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } };

const ProductGroupManagement: React.FC = () => {
  const auth = useAuth();
  const [groups, setGroups] = useState<ProductGroupListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [view, setView] = useState<'list' | 'tree'>('list');
  // Bumped after every edit so the Tree tab reloads (search alone doesn't).
  const [treeVersion, setTreeVersion] = useState(0);

  const [activeIngredients, setActiveIngredients] = useState<LookupItem[]>([]);
  const [units, setUnits] = useState<LookupItem[]>([]);
  const [allGroups, setAllGroups] = useState<LookupItem[]>([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [parentProductGroupId, setParentProductGroupId] = useState('');
  const [childProductGroups, setChildProductGroups] = useState<{ id: string; name: string }[]>([]);
  const [aiRows, setAiRows] = useState<ProductGroupActiveIngredient[]>([]);
  const [memberProducts, setMemberProducts] = useState<{ id: string; name: string }[]>([]);
  const [suggestedProducts, setSuggestedProducts] = useState<SuggestedProduct[]>([]);
  const [newAiId, setNewAiId] = useState('');
  const [newUnitId, setNewUnitId] = useState('');
  const [newUnitAmount, setNewUnitAmount] = useState('0');

  // Manual "add any product as a member" search — separate from
  // Suggested Products (which only surfaces products sharing an active
  // ingredient with this group), since a product with no/incomplete active
  // ingredient data would otherwise never show up to be assigned.
  const [memberProductOptions, setMemberProductOptions] = useState<MemberProductOption[]>([]);
  const [newMemberProduct, setNewMemberProduct] = useState<MemberProductOption | null>(null);
  const memberProductDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` });

  const handleMemberProductSearchInput = (value: string) => {
    if (memberProductDebounceRef.current) clearTimeout(memberProductDebounceRef.current);
    memberProductDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ pageSize: '25' });
        if (value.trim()) params.set('search', value.trim());
        const response = await fetch(`/api/products?${params.toString()}`, { headers: authHeaders() });
        if (!response.ok) return;
        const data = await response.json();
        setMemberProductOptions(
          (data.items ?? []).map((p: { id: string; name: string; activeIngredientDetails?: string[] }) => ({
            id: p.id,
            name: p.name,
            activeIngredientDetails: p.activeIngredientDetails ?? [],
          }))
        );
      } catch {
        // best-effort — Autocomplete just shows no results if this fails
      }
    }, 350);
  };

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        includeArchived: String(includeArchived),
        page: String(page + 1),
        pageSize: String(pageSize),
      });
      if (search) params.set('search', search);
      const response = await fetch(`/api/product-groups?${params.toString()}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch product groups');
      const data = await response.json();
      setGroups(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [includeArchived, page, pageSize, search, auth.tokens?.accessToken]);

  const refreshAfterChange = async () => {
    setTreeVersion((v) => v + 1);
    await fetchGroups();
  };

  const fetchLookups = async () => {
    try {
      const [aiRes, unitRes, groupRes] = await Promise.all([
        fetch('/api/active-ingredients', { headers: authHeaders() }),
        fetch('/api/units', { headers: authHeaders() }),
        fetch('/api/product-groups?pageSize=500', { headers: authHeaders() }),
      ]);
      if (aiRes.ok) setActiveIngredients(await aiRes.json());
      if (unitRes.ok) setUnits(await unitRes.json());
      if (groupRes.ok) {
        const data = await groupRes.json();
        setAllGroups((data.items ?? []).map((g: { id: string; name: string }) => ({ id: g.id, name: g.name })));
      }
    } catch {
      // best-effort — dropdowns just stay empty if this fails
    }
  };

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) fetchGroups();
  }, [fetchGroups, auth.isSignedIn]);

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) fetchLookups();
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  const fetchGroupDetail = async (id: string): Promise<ProductGroupDetail | null> => {
    const response = await fetch(`/api/product-groups/${id}`, { headers: authHeaders() });
    if (!response.ok) return null;
    return response.json();
  };

  const fetchSuggested = async (id: string) => {
    try {
      const response = await fetch(`/api/product-groups/${id}/suggested-products`, { headers: authHeaders() });
      if (response.ok) setSuggestedProducts(await response.json());
    } catch {
      // best-effort
    }
  };

  const handleOpenDialog = async (groupId?: string) => {
    if (groupId) {
      const detail = await fetchGroupDetail(groupId);
      if (!detail) {
        setError('Failed to load product group details');
        return;
      }
      setEditingGroupId(detail.id);
      setName(detail.name);
      setParentProductGroupId(detail.parentProductGroupId ?? '');
      setChildProductGroups(detail.childProductGroups);
      setAiRows(detail.activeIngredients);
      setMemberProducts(detail.memberProducts);
      await fetchSuggested(detail.id);
    } else {
      setEditingGroupId(null);
      setName('');
      setParentProductGroupId('');
      setChildProductGroups([]);
      setAiRows([]);
      setMemberProducts([]);
      setSuggestedProducts([]);
    }
    setNewAiId('');
    setNewUnitId('');
    setNewUnitAmount('0');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGroupId(null);
    setName('');
    setParentProductGroupId('');
    setChildProductGroups([]);
    setAiRows([]);
    setMemberProducts([]);
    setSuggestedProducts([]);
  };

  const handleAddActiveRow = async () => {
    if (!newAiId || !newUnitId || !editingGroupId) return;
    if (aiRows.some((r) => r.activeIngredientId === newAiId)) {
      setError('This active ingredient is already added.');
      return;
    }
    try {
      const response = await fetch(`/api/product-groups/${editingGroupId}/active-ingredients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ activeIngredientId: newAiId, unitId: newUnitId, unitAmount: Number(newUnitAmount), isVisible: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const fieldErrors = data.errors ? Object.values(data.errors).flat() : [];
        throw new Error(data.error || fieldErrors.join(' ') || 'Failed to add active ingredient');
      }
      setAiRows((prev) => [...prev, data]);
      await fetchSuggested(editingGroupId);
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return;
    }
    setNewAiId('');
    setNewUnitId('');
    setNewUnitAmount('0');
  };

  const handleRemoveActiveRow = async (row: ProductGroupActiveIngredient) => {
    if (!editingGroupId) return;
    try {
      const response = await fetch(`/api/product-groups/${editingGroupId}/active-ingredients/${row.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to remove active ingredient');
      setAiRows((prev) => prev.filter((r) => r.id !== row.id));
      await fetchSuggested(editingGroupId);
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleAssignProduct = async (productId: string) => {
    if (!editingGroupId) return;
    try {
      const response = await fetch(`/api/product-groups/${editingGroupId}/products/${productId}`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to assign product');
      // Re-fetch rather than patch local state optimistically — this is
      // called both from Suggested Products (id already known locally) and
      // the manual search box (a product that may never have appeared in
      // suggestedProducts at all), so a server refresh is the only path
      // that's correct for both.
      const detail = await fetchGroupDetail(editingGroupId);
      if (detail) setMemberProducts(detail.memberProducts);
      await fetchSuggested(editingGroupId);
      setNewMemberProduct(null);
      setMemberProductOptions([]);
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleUnassignProduct = async (productId: string) => {
    if (!editingGroupId) return;
    try {
      const response = await fetch(`/api/product-groups/${editingGroupId}/products/${productId}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to unassign product');
      setMemberProducts((prev) => prev.filter((p) => p.id !== productId));
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingGroupId ? `/api/product-groups/${editingGroupId}` : '/api/product-groups';
      const method = editingGroupId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name, parentProductGroupId: parentProductGroupId || null }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const fieldErrors = errorData.errors ? Object.values(errorData.errors).flat() : [];
        throw new Error(errorData.error || fieldErrors.join(' ') || 'Failed to save product group');
      }
      handleCloseDialog();
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Archive this product group?')) return;
    try {
      const response = await fetch(`/api/product-groups/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to archive product group');
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(`/api/product-groups/${id}/restore`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to restore product group');
      await refreshAfterChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Not while searching: a search with no list hits would otherwise blank
  // the whole screen (Tree tab included) on every keystroke.
  if (loading && groups.length === 0 && !search) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <CategoryIcon />
          Product Groups
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControlLabel
            control={<Switch checked={includeArchived} onChange={(e) => { setPage(0); setIncludeArchived(e.target.checked); }} />}
            label="Show Archived"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Product Group
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A generic "roughly these active ingredients" bucket for import rows too vague to match to one specific product —
        selectable as the Product in the import wizard instead of a real Product.
      </Typography>

      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="list" label="List" />
        <Tab value="tree" label="Tree" />
      </Tabs>

      <TextField
        fullWidth
        size="small"
        placeholder={view === 'tree' ? 'Search groups or member products...' : 'Search by group name...'}
        value={search}
        onChange={(e) => { setPage(0); setSearch(e.target.value); }}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {view === 'tree' ? (
        <ProductGroupTree
          includeArchived={includeArchived}
          search={search}
          refreshKey={treeVersion}
          onEdit={(id) => handleOpenDialog(id)}
        />
      ) : groups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No product groups found.</Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Parent Group</TableCell>
                  <TableCell>Active Ingredients</TableCell>
                  <TableCell align="right">Member Products</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.parentProductGroupName || '-'}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {group.activeIngredientDetails.length === 0
                          ? '-'
                          : group.activeIngredientDetails.map((ai) => (
                              <Chip key={ai} label={ai} size="small" color="primary" variant="outlined" sx={chipSx} />
                            ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{group.memberProductCount}</TableCell>
                    <TableCell>
                      {group.archivedDate ? (
                        <Chip label="Archived" size="small" color="default" sx={chipSx} />
                      ) : (
                        <Chip label="Active" size="small" color="success" sx={chipSx} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {group.archivedDate ? (
                        <Tooltip title="Restore Product Group">
                          <IconButton size="small" onClick={() => handleRestore(group.id)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Edit Product Group">
                            <IconButton size="small" onClick={() => handleOpenDialog(group.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive Product Group">
                            <IconButton size="small" color="error" onClick={() => handleArchive(group.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingGroupId ? 'Edit Product Group' : 'Add Product Group'}
          <IconButton size="small" onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Group Name" value={name} onChange={(e) => setName(e.target.value)} required />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={allGroups.filter((g) => g.id !== editingGroupId)}
                getOptionLabel={(g) => g.name}
                isOptionEqualToValue={(g, v) => g.id === v.id}
                value={allGroups.find((g) => g.id === parentProductGroupId) ?? null}
                onChange={(_, value) => setParentProductGroupId(value?.id ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Group (optional)"
                    helperText="For rolling several specific groups up into one broad bucket for reporting — e.g. this group's usage counts toward its parent's total too."
                  />
                )}
              />
            </Grid>

            {childProductGroups.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Rolls up these groups: {childProductGroups.map((c) => c.name).join(', ')}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Active Ingredients</Typography>
              {!editingGroupId && (
                <Alert severity="info" sx={{ mb: 2 }}>Save the group first, then add its active ingredients.</Alert>
              )}
              {editingGroupId && (
                <>
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Active Ingredient</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aiRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" py={2}>No active ingredients added yet.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          aiRows.map((pai) => (
                            <TableRow key={pai.id}>
                              <TableCell>{pai.activeIngredientName}</TableCell>
                              <TableCell>{pai.unitAmount}</TableCell>
                              <TableCell>{pai.unitName}</TableCell>
                              <TableCell align="right">
                                <IconButton size="small" color="error" onClick={() => handleRemoveActiveRow(pai)}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <Autocomplete
                        size="small"
                        options={activeIngredients}
                        getOptionLabel={(ai) => ai.name}
                        isOptionEqualToValue={(ai, v) => ai.id === v.id}
                        filterOptions={filterActiveIngredientOptions}
                        value={activeIngredients.find((ai) => ai.id === newAiId) ?? null}
                        onChange={(_, value) => setNewAiId(value?.id ?? '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Active Ingredient"
                            helperText="Many active ingredients differ only by salt (e.g. Glyphosate Isopropylamine vs Potassium) — the g/L or g/kg strength is set below, in Amount."
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth size="small" label="Amount" type="number" value={newUnitAmount} onChange={(e) => setNewUnitAmount(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select value={newUnitId} label="Unit" onChange={(e) => setNewUnitId(e.target.value)}>
                          {units.map((u) => (
                            <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="primary" onClick={handleAddActiveRow} disabled={!newAiId || !newUnitId}>
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>

            {editingGroupId && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Member Products</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Real products identified as belonging to this generic bucket — purely for browsing, doesn't affect import matching.
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {memberProducts.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">None assigned yet.</Typography>
                    ) : (
                      memberProducts.map((p) => (
                        <Chip key={p.id} label={p.name} size="small" onDelete={() => handleUnassignProduct(p.id)} />
                      ))
                    )}
                  </Box>

                  <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={9}>
                      <Autocomplete
                        size="small"
                        options={memberProductOptions}
                        getOptionLabel={(p) => p.name}
                        isOptionEqualToValue={(p, v) => p.id === v.id}
                        filterOptions={(opts) => opts}
                        value={newMemberProduct}
                        onChange={(_, value) => setNewMemberProduct(value)}
                        onInputChange={(_, value) => handleMemberProductSearchInput(value)}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            <Box>
                              <Box>{option.name}</Box>
                              {option.activeIngredientDetails && option.activeIngredientDetails.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  {option.activeIngredientDetails.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Add a product by name, active ingredient, or unit"
                            helperText={
                              newMemberProduct?.activeIngredientDetails && newMemberProduct.activeIngredientDetails.length > 0
                                ? newMemberProduct.activeIngredientDetails.join(', ')
                                : 'For a product not listed under Suggested below (e.g. missing active-ingredient data).'
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={!newMemberProduct}
                        onClick={() => newMemberProduct && handleAssignProduct(newMemberProduct.id)}
                      >
                        Add Member
                      </Button>
                    </Grid>
                  </Grid>

                  {suggestedProducts.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>Suggested Products</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Real products that share at least one of this group's active ingredients.
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>Manufacturer</TableCell>
                              <TableCell>Active Ingredient</TableCell>
                              <TableCell>Matching Actives</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {suggestedProducts.map((sp) => (
                              <TableRow key={sp.id}>
                                <TableCell>{sp.name}</TableCell>
                                <TableCell>{sp.manufacturerName || '-'}</TableCell>
                                <TableCell>
                                  {sp.activeIngredientDetails.length > 0 ? sp.activeIngredientDetails.join(', ') : '-'}
                                </TableCell>
                                <TableCell>{sp.matchingActiveIngredientCount}</TableCell>
                                <TableCell align="right">
                                  <Button size="small" onClick={() => handleAssignProduct(sp.id)}>Assign</Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!name.trim()}>
            {editingGroupId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductGroupManagement;
